import type { MicrophoneErrorKind, MicrophonePermissionState } from "shared";

/**
 * Browser microphone access, for any feature that records the mic.
 *
 * There is no permission API to "ask" with: the browser shows its own prompt
 * the first time `getUserMedia` is called, and remembers the answer per site.
 * These helpers wrap that call, read the remembered answer ahead of time, turn
 * the browser's errors into something the UI can word, and release the mic.
 * The mic only works on HTTPS (or localhost).
 */

/** Mono, with the browser's echo cancellation and noise suppression — right for speech. */
const SPEECH_CONSTRAINTS: MediaTrackConstraints = {
  channelCount: 1,
  echoCancellation: true,
  noiseSuppression: true,
};

export const isMicrophoneSupported = (): boolean =>
  typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia;

/**
 * The browser's remembered answer, read without prompting. Use it to decide
 * whether starting will show a prompt: e.g. hold back a short-lived token
 * until the user has actually allowed the mic.
 */
export const getMicrophonePermissionState =
  async (): Promise<MicrophonePermissionState> => {
    try {
      // "microphone" is a real permission name that TypeScript's DOM lib omits.
      const status = await navigator.permissions.query({
        name: "microphone" as PermissionName,
      });
      return status.state;
    } catch {
      return "unknown";
    }
  };

/**
 * Opens the mic, showing the browser's prompt if the user hasn't answered yet.
 * Rejects with the browser's error — pass it to `toMicrophoneErrorKind`.
 */
export const openMicrophone = (
  constraints: MediaTrackConstraints = SPEECH_CONSTRAINTS,
): Promise<MediaStream> =>
  navigator.mediaDevices.getUserMedia({ audio: constraints });

/** Stops every track, which also turns off the browser's "mic in use" indicator. */
export const releaseMicrophone = (stream: MediaStream): void => {
  for (const track of stream.getTracks()) track.stop();
};

export const toMicrophoneErrorKind = (error: unknown): MicrophoneErrorKind => {
  const name = error instanceof DOMException ? error.name : "";
  if (name === "NotAllowedError" || name === "SecurityError") {
    return "permission_denied";
  }
  if (name === "NotFoundError" || name === "OverconstrainedError") {
    return "no_microphone";
  }
  return "unavailable";
};
